/**
 * `Lead.customFields` is typed `Record<string, unknown>`, but it maps to a
 * Postgres JSONB column, so the API can hand back any JSON value — including a
 * bare string. The lead detail screen enumerates the object with
 * `Object.entries`, so a string arrives as one row per character.
 *
 * Backend merge sites used to spread that value (`{ ...customFields }`), which
 * persisted the same decomposition as real JSONB keys: '0', '1', '2'… Those
 * rows heal on their next write, but until then this normalizes what the screen
 * reads.
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseObjectString(value: string): Record<string, unknown> | undefined {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('"')) return undefined;

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (isPlainObject(parsed)) return parsed;
    if (typeof parsed === 'string') return parseObjectString(parsed);
  } catch {
    return undefined;
  }

  return undefined;
}

/**
 * Reassembles an object whose keys are exactly the contiguous indices of a
 * spread string. Deliberately narrow: every key must be its own index, every
 * value a single character, and the joined result must itself parse to an
 * object — a legitimate payload cannot satisfy all three.
 */
function repairSpreadString(obj: Record<string, unknown>): Record<string, unknown> | undefined {
  const keys = Object.keys(obj);
  if (keys.length < 2) return undefined;

  for (const [index, key] of keys.entries()) {
    if (key !== String(index)) return undefined;
    const character = obj[key];
    if (typeof character !== 'string' || character.length !== 1) return undefined;
  }

  return parseObjectString(keys.map((key) => obj[key] as string).join(''));
}

/** Coerces a persisted `customFields` value into the object the screen expects. */
export function asCustomFields(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') return parseObjectString(value) ?? {};
  if (!isPlainObject(value)) return {};

  return repairSpreadString(value) ?? value;
}
